// src/features/settings/components/non-working-days-panel/NonWorkingDaysGrid.jsx
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, IconButton } from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  BeachAccess as HolidayIcon,
  EventBusy as ClosingDayIcon,
  CalendarToday as CalendarIcon,
  Repeat as RepeatIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { format } from 'date-fns';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), 'MMM dd, yyyy');
  } catch {
    return dateString;
  }
};

export default function NonWorkingDaysGrid({
  rows,
  loading,
  totalCount,
  paginationModel,
  onPaginationModelChange,
  canUpdate,
  canDelete,
  onEdit,
  onDelete,
}) {
  const { t } = useTranslation(['settings', 'common']);

  const columns = useMemo(() => [
    {
      field: 'kind',
      headerName: t('settings:non_working_days.grid.kind'),
      width: 150,
      renderCell: (params) => (
        <Chip
          icon={params.value === 'PUBLIC_HOLIDAY' ? <HolidayIcon /> : <ClosingDayIcon />}
          label={
            params.value === 'PUBLIC_HOLIDAY'
              ? t('settings:non_working_days.types.public_holiday')
              : t('settings:non_working_days.types.closing_day')
          }
          size="small"
          color={params.value === 'PUBLIC_HOLIDAY' ? 'secondary' : 'primary'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'name',
      headerName: t('settings:non_working_days.grid.name'),
      flex: 1.5,
      minWidth: 180,
    },
    {
      field: 'dates',
      headerName: t('settings:non_working_days.grid.dates'),
      flex: 1.5,
      minWidth: 220,
      sortable: false,
      renderCell: (params) => {
        const row = params.row;
        if (row.rrule) {
          return (
            <Chip
              icon={<RepeatIcon />}
              label={row.rrule}
              size="small"
              variant="outlined"
            />
          );
        }
        if (row.start_date && row.end_date && row.start_date !== row.end_date) {
          return `${formatDate(row.start_date)} – ${formatDate(row.end_date)}`;
        }
        return (
          <Box display="flex" alignItems="center" gap={0.5}>
            <CalendarIcon fontSize="small" color="disabled" />
            {formatDate(row.start_date)}
          </Box>
        );
      },
    },
    {
      field: 'is_full_day',
      headerName: t('settings:non_working_days.grid.is_full_day'),
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('common:yes') : t('common:no')}
          color={params.value ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'free_from',
      headerName: t('settings:non_working_days.grid.free_from'),
      width: 110,
      renderCell: (params) => params.value ?? '—',
    },
    {
      field: 'auto_generated',
      headerName: t('settings:non_working_days.grid.auto_generated'),
      width: 130,
      renderCell: (params) => (params.value ? t('common:yes') : t('common:no')),
    },
    {
      field: 'actions',
      headerName: t('common:grid.actions'),
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          {canUpdate && (
            <IconButton
              size="small"
              onClick={() => onEdit(params.row)}
              title={t('common:buttons.edit')}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          {canDelete && (
            <IconButton
              size="small"
              onClick={() => onDelete(params.row)}
              title={t('common:buttons.delete')}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ], [t, canUpdate, canDelete, onEdit, onDelete]);

  return (
    <DataGrid
      rows={rows}
      columns={columns}
      getRowId={(row) => row.non_working_day_id}
      loading={loading}
      pageSizeOptions={[5, 10, 25, 50]}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      rowCount={totalCount}
      paginationMode="server"
      disableRowSelectionOnClick
      autoHeight
      sx={{
        minHeight: 400,
        '& .MuiDataGrid-cell': {
          minHeight: 'unset !important',
          maxHeight: 'none !important',
          height: 'auto !important',
          display: 'flex !important',
          alignItems: 'center !important',
          lineHeight: '1.5 !important',
          padding: '12px 16px !important',
        },
      }}
    />
  );
}
